import { useState } from "react";
import {
  Keyboard,
  Link as LinkIcon,
} from "lucide-react";
import type { SortOption } from "@wizard-sounds/shared";
import { SORT_OPTIONS } from "@wizard-sounds/shared";

export function AdminSettings() {
  const [defaultSort, setDefaultSort] = useState<SortOption>(() => {
    return (localStorage.getItem("wizard-sounds-sort") as SortOption) || "newest";
  });

  const handleSortChange = (sort: SortOption) => {
    setDefaultSort(sort);
    localStorage.setItem("wizard-sounds-sort", sort);
  };

  return (
    <div>
      <h1 className="font-display text-2xl font-bold tracking-display">Settings</h1>

      <div className="mt-6 space-y-6">
        {/* ── Default Sort Order ── */}
        <div className="rounded-[18px] border border-mist bg-slate p-4">
          <h2 className="font-medium text-parchment">Default Sort Order</h2>
          <p className="mt-1 text-sm text-fog">
            Sets the default sort when the soundboard loads.
          </p>
          <select
            value={defaultSort}
            onChange={(e) => handleSortChange(e.target.value as SortOption)}
            className="mt-3 rounded-xl border border-mist bg-ink px-3 py-1.5 text-sm text-parchment outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt.charAt(0).toUpperCase() + opt.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* ── Keyboard Shortcuts ── */}
        <div className="rounded-[18px] border border-mist bg-slate p-4">
          <h2 className="flex items-center gap-2 font-medium text-parchment">
            <Keyboard className="h-4 w-4 text-fog" />
            Keyboard Shortcuts
          </h2>
          <p className="mt-1 text-sm text-fog">
            Hotkeys active on the public soundboard.
          </p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center justify-between rounded-xl bg-slate-raised px-3 py-2">
              <span className="text-parchment">Stop all sounds</span>
              <kbd className="rounded-lg bg-ink px-2 py-0.5 font-mono text-xs text-fog">
                Space
              </kbd>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-slate-raised px-3 py-2">
              <span className="text-parchment">Quick play sounds 1 through 9</span>
              <kbd className="rounded-lg bg-ink px-2 py-0.5 font-mono text-xs text-fog">
                1-9
              </kbd>
            </div>
          </div>
        </div>

        {/* ── Integrations ── */}
        <div className="rounded-[18px] border border-mist bg-slate p-4">
          <h2 className="flex items-center gap-2 font-medium text-parchment">
            <LinkIcon className="h-4 w-4 text-fog" />
            Integrations
          </h2>
          <p className="mt-1 text-sm text-fog">
            Use these URLs to integrate Wizard Sounds with external tools.
          </p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="rounded-xl bg-slate-raised px-3 py-2">
              <span className="text-fog">Trigger URL pattern:</span>
              <code className="ml-2 font-mono text-xs text-spark">
                {window.location.origin}/trigger/:slug
              </code>
            </div>
            <div className="rounded-xl bg-slate-raised px-3 py-2">
              <span className="text-fog">Board mode:</span>
              <code className="ml-2 font-mono text-xs text-spark">
                {window.location.origin}/board
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
